import React from 'react'
import { signedPoints, deltaClass } from '../lib/format'

// Diverging bars centered on zero: gains extend right, losses left. A shared
// scale across every row is what makes the rows comparable to each other —
// per-row scaling would make a 0.2 loss look as dramatic as a 9 point one.
//
// The scale is per-render, so switching between a single week and the season
// average rescales the bars. That is correct: the two are different questions
// and the numbers beside each bar say which is being asked.
//
// netLabel exists because the unit changes with the scope — a season figure is
// points *per week*, a single week's is just points, and labelling both the
// same way would misreport one of them.
export default function PositionImpact({ byPosition, net, netLabel = 'points per week' }) {
  const entries = Object.entries(byPosition || {})

  if (entries.length === 0) {
    return <p className="text-sm text-slate-400">No positional change.</p>
  }

  const magnitude = Math.max(...entries.map(([, value]) => Math.abs(value)), 0.1)
  const total = net ?? entries.reduce((sum, [, value]) => sum + value, 0)

  return (
    <div>
      <ul className="space-y-1.5">
        {entries.map(([slot, value]) => (
          <li key={slot} className="flex items-center gap-3 text-sm">
            <span className="w-14 shrink-0 font-medium text-slate-300">
              {slot}
            </span>

            <span className={`w-14 shrink-0 text-right tabular-nums ${deltaClass(value)}`}>
              {signedPoints(value)}
            </span>

            <span className="relative h-4 flex-1" aria-hidden="true">
              <span className="absolute inset-y-0 left-1/2 w-px bg-[var(--diverging-axis)]" />
              <span
                className="absolute inset-y-0.5 rounded-sm"
                style={{
                  background: value >= 0 ? 'var(--gain)' : 'var(--loss)',
                  width: `${(Math.abs(value) / magnitude) * 50}%`,
                  ...(value >= 0 ? { left: '50%' } : { right: '50%' }),
                }}
              />
            </span>
          </li>
        ))}
      </ul>

      <div className="mt-3 flex items-center gap-3 border-t pt-3 text-sm border-slate-700">
        <span className="w-14 shrink-0 font-semibold text-slate-200">NET</span>
        <span className={`w-14 shrink-0 text-right font-semibold tabular-nums ${deltaClass(total)}`}>
          {signedPoints(total)}
        </span>
        <span className="text-xs text-slate-400">{netLabel}</span>
      </div>
    </div>
  )
}
